import type { SubscriptionTier } from "@/types/projects";

export type AiProviderKind = "openai" | "anthropic" | "local";
export type AiRequestStatus = "error" | "idle" | "loading" | "ready";
export type AiFeatureAction = "chat" | "continuation" | "continuity" | "enhance" | "polish" | "structural";
export type AiAccessPromptKind =
  | "budget-restricted"
  | "daily-limit"
  | "top-up"
  | "upgrade"
  | "wait";
export type AiChatMessageRole = "assistant" | "user";
export type AiContextCacheStatus = "hit" | "miss" | "not-applicable";
export type AiGuardNoticeKind =
  | "budget-restricted"
  | "cache-hit"
  | "rate-limit"
  | "service-busy"
  | "truncation";
export type ContinuityIssueSeverity = "critical" | "low" | "medium";
export type ContinuityIssueResolutionStatus =
  | "acknowledged"
  | "dismissed"
  | "open"
  | "resolved";

export interface ProjectAiSettingsDraft {
  baseUrl: string;
  modelName: string;
  providerKind: AiProviderKind;
  secret: string;
}

export interface ProjectAiSettingsView {
  connectionStatusLabel: string;
  lastTestMessage: string | null;
  providerKind: AiProviderKind;
  providerLabel: string;
  secretPresent: boolean;
}

export interface AiEntitlementView {
  creditCost: number;
  creditsRemaining: number | null;
  tier: SubscriptionTier;
  usesRemainingToday: number | null;
}

export interface AiAccessPromptView {
  ctaLabelKey: string | null;
  descriptionKey: string;
  descriptionValues?: Record<string, number | string | null | undefined>;
  kind: AiAccessPromptKind;
  recommendedTier?: SubscriptionTier | null;
}

export interface AiGuardNoticeView {
  creditsSafe?: boolean;
  detailKey?: string | null;
  feature?: AiFeatureAction | null;
  kind: AiGuardNoticeKind;
  messageKey: string;
  minutesUntilReset?: number | null;
  resetAt?: string | null;
  retryAfterSeconds?: number | null;
}

export interface AiGuardOutcomeView {
  cacheStatus: AiContextCacheStatus;
  creditsSafe: boolean;
  notices: AiGuardNoticeView[];
  resetAt: string | null;
  retryAfterSeconds: number | null;
  stage?: string | null;
  truncated: boolean;
}

export interface AiChatCitationView {
  evidenceNote?: string | null;
  openHref?: string | null;
  sourceKind: "chapter" | "lore_entry";
  sourceName: string;
  sourceRecordId?: string | null;
}

export interface AiChatExchangeView {
  answer: string;
  citations: AiChatCitationView[];
  id: string;
  isStreaming?: boolean;
  question: string;
  ungroundedNotice?: string | null;
}

export interface AiChatSidebarState {
  activeChapterLabel: string | null;
  creditsRemaining: number | null;
  errorMessage: string | null;
  exchanges: AiChatExchangeView[];
  guardOutcome: AiGuardOutcomeView | null;
  loreSummary: string;
  open: boolean;
  projectTitle: string;
  prompt: AiAccessPromptView | null;
  requestStatus: AiRequestStatus;
}

export interface AiChatMessageInput {
  content: string;
  role: AiChatMessageRole;
}

export interface AiChatStreamResult {
  answer: string;
  citations: AiChatCitationView[];
  creditsRemaining: number | null;
  guardOutcome: AiGuardOutcomeView;
}

export interface ContinuationSuggestionOptionView {
  id: string;
  text: string;
}

export interface ContinuationSuggestionSessionView {
  chapterId: string;
  createdAt: string;
  id: string;
  options: ContinuationSuggestionOptionView[];
}

export type ContinuationUsageView = AiEntitlementView;

export interface ContinuationSuggestionMutationResult {
  guardOutcome: AiGuardOutcomeView;
  session: ContinuationSuggestionSessionView;
  usage: ContinuationUsageView;
}

export interface ContinuityIssueCitationView {
  canonStatusLabel?: string | null;
  evidenceSummary: string;
  id: string;
  openHref?: string | null;
  sourceKind: "chapter" | "lore_entry";
  sourceNameSnapshot: string;
  sourceRecordId?: string | null;
}

export interface ContinuityIssueView {
  citations: ContinuityIssueCitationView[];
  description: string;
  id: string;
  resolutionStatus: ContinuityIssueResolutionStatus;
  resolutionStatusLabel: string;
  severity: ContinuityIssueSeverity;
  severityLabel: string;
}

export interface ContinuityReviewView {
  chapterId: string;
  creditsRemaining: number | null;
  guardOutcome: AiGuardOutcomeView;
  issues: ContinuityIssueView[];
  noCanonContext: boolean;
  retrievedCanonCount: number;
  reviewSummary: string;
}

export type StructuralAnalysisDimensionName =
  | "chapter_balance"
  | "character_voice_consistency"
  | "pacing"
  | "structural_gaps"
  | "tension_arc";

export type StructuralAnalysisCalloutReviewState =
  | "acknowledged"
  | "dismissed"
  | "open";

export interface StructuralAnalysisScopeSnapshotView {
  chapterCount: number;
  chapterIds: string[];
  endChapterId: string | null;
  scopeKind: "chapter-range" | "full-story";
  scopeLabelSnapshot: string;
  startChapterId: string | null;
}

export interface StructuralAnalysisCalloutView {
  chapterId: string;
  chapterHref?: string | null;
  chapterLabel: string;
  dimension: StructuralAnalysisDimensionName;
  dimensionLabel: string;
  findingIndex: number;
  id: string;
  reviewState: StructuralAnalysisCalloutReviewState;
  reviewStateLabel: string;
  runId: string;
  text: string;
}

export interface StructuralAnalysisSectionView {
  dimension: StructuralAnalysisDimensionName;
  dimensionLabel: string;
  emptyStateLabel: string;
  findings: StructuralAnalysisCalloutView[];
  id: string;
}

export interface StructuralAnalysisRunView {
  chapterCount: number;
  createdAt: string;
  creditsUsed: number;
  id: string;
  scope: StructuralAnalysisScopeSnapshotView;
  scopeLabelSnapshot: string;
  sections: StructuralAnalysisSectionView[];
  storyId: string | null;
}

export interface StructuralAnalysisHistoryEntryView {
  chapterCount: number;
  createdAt: string;
  creditsUsed: number;
  previewText: string;
  runId: string;
  scopeLabel: string;
}

export interface StructuralAnalysisMutationResult {
  creditsRemaining: number | null;
  guardOutcome: AiGuardOutcomeView;
  run: StructuralAnalysisRunView;
}

export interface StructuralAnalysisStatusMutationResult {
  dimensionName: StructuralAnalysisDimensionName;
  findingIndex: number;
  runId: string;
  status: StructuralAnalysisCalloutReviewState;
}

export type TextEnhancementStatus = "idle" | "loading" | "suggestion_ready" | "error";

export type DiffSegment = {
  index: number;
  kind: "added" | "removed" | "unchanged";
  text: string;
};

export type EnhanceSourceContext = {
  chapterTitle: string;
  genre: string | null;
  language: string | null;
  styleGuideLoreEntryId?: string | null;
  styleGuideUsed: boolean;
  truncated: boolean;
};

export type PolishSourceContext = {
  chapterTitle: string;
  includedWordCount: number | null;
  language: string | null;
  originalWordCount: number | null;
  truncated: boolean;
};

export interface EnhanceSuggestionResult {
  chapterId: string;
  creditCost: number;
  creditsRemaining: number | null;
  noImprovements: boolean;
  sourceContext: EnhanceSourceContext | null;
  suggestion: string | null;
}

export interface PolishSuggestionResult {
  chapterId: string;
  creditCost: number;
  creditsRemaining: number | null;
  diff: DiffSegment[] | null;
  noImprovements: boolean;
  originalText: string | null;
  sourceContext: PolishSourceContext | null;
  suggestion: string | null;
}

export const aiProviderKinds: AiProviderKind[] = ["openai", "anthropic", "local"];

export const aiProviderLabels: Record<AiProviderKind, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  local: "Local model server",
};

export const continuityIssueResolutionOrder: ContinuityIssueResolutionStatus[] = [
  "open",
  "acknowledged",
  "resolved",
  "dismissed",
];

export function providerRequiresSecret(providerKind: AiProviderKind) {
  return providerKind !== "local";
}

export function providerUsesBaseUrl(providerKind: AiProviderKind) {
  return providerKind === "local";
}
